import { oc } from "@orpc/contract";
import { z } from "zod";
import { listInput, listPage } from "./list";

/**
 * A slug as it appears in a URL: lower-case letters and digits, words joined by single hyphens
 * (`over-ons`, `retourneren`).
 */
export const Slug = z
	.string()
	.trim()
	.min(1)
	.max(120)
	.regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "lower-case letters, digits and single hyphens");

/** An optional line of text: empty or missing is `null`. */
const optionalText = (max: number) =>
	z
		.string()
		.trim()
		.max(max)
		.nullish()
		.transform((value) => value || null);

/** A page as the admin edits it. `body` is markdown, stored as typed; the shop renders it. */
export const Page = z.object({
	id: z.string(),
	title: z.string(),
	slug: z.string(),
	body: z.string(),
	seoTitle: z.string().nullable(),
	seoDescription: z.string().nullable(),
	published: z.boolean(),
	/** Goes up by one on every save; an update sends the version it was loaded at. */
	version: z.number().int(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
});
export type Page = z.infer<typeof Page>;

/**
 * The admin's page list: searched on title and slug, filtered on published, sorted by title or by
 * when it was last saved.
 */
export const PageListInput = listInput({
	sort: ["title", "updatedAt"],
	defaultSort: "title",
	filters: { published: z.enum(["yes", "no"]).optional().catch(undefined) },
});
export type PageListInput = z.input<typeof PageListInput>;
/** The list query with its defaults filled in, as the API receives it. */
export type PageListQuery = z.output<typeof PageListInput>;

export const PageList = listPage(Page);
export type PageList = z.infer<typeof PageList>;

/** What the admin's page form sends to create a page. */
export const PageFields = z.object({
	title: z.string().trim().min(1).max(200),
	slug: Slug,
	body: z.string().max(200_000),
	seoTitle: optionalText(200),
	seoDescription: optionalText(500),
	published: z.boolean(),
});

/**
 * What the form sends to save a page: every field, plus the `version` it loaded. A page saved by
 * someone else in the meantime is refused (409), not overwritten.
 */
export const PageUpdate = PageFields.extend({ version: z.number().int().positive() });

/** A published page in the shop's list (a menu, the sitemap): everything but the body. */
export const PublishedPageSummary = z.object({
	slug: z.string(),
	title: z.string(),
	seoTitle: z.string().nullable(),
	seoDescription: z.string().nullable(),
	updatedAt: z.iso.datetime(),
});
export type PublishedPageSummary = z.infer<typeof PublishedPageSummary>;

/** A published page as the shop shows it. */
export const PublishedPage = PublishedPageSummary.extend({ body: z.string() });
export type PublishedPage = z.infer<typeof PublishedPage>;

const ById = z.object({ id: z.string() });

/**
 * Pages Richard edits (about us, returns, terms). `/admin/...` needs a staff session; `/shop/...`
 * is public and only ever shows published pages: an unpublished page is a 404 there.
 */
export const PageContract = {
	list: oc.route({ method: "GET", path: "/admin/pages" }).input(PageListInput).output(PageList),
	create: oc
		.route({ method: "POST", path: "/admin/pages", successStatus: 201 })
		.input(PageFields)
		.output(Page),
	get: oc.route({ method: "GET", path: "/admin/pages/{id}" }).input(ById).output(Page),
	update: oc
		.route({ method: "PUT", path: "/admin/pages/{id}" })
		.input(PageUpdate.extend(ById.shape))
		.output(Page),
	delete: oc.route({ method: "DELETE", path: "/admin/pages/{id}", successStatus: 204 }).input(ById),
	listPublished: oc.route({ method: "GET", path: "/shop/pages" }).output(z.array(PublishedPageSummary)),
	getPublished: oc
		.route({ method: "GET", path: "/shop/pages/{slug}" })
		.input(z.object({ slug: z.string() }))
		.output(PublishedPage),
};
